import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Globe } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Browser-provided country name (never hard-coded here); falls back to the 2-letter code.
export const countryLabel = (code) => {
  if (!code) return '';
  try { return new Intl.DisplayNames(['en'], { type: 'region' }).of(code) || code; } catch (e) { return code; }
};

// Shown on the sign-up screen only, when the country gate says the visitor's country is not on the allowed list.
export const CountryGateNotice = ({ gate }) => {
  const [email, setEmail] = useState('');
  const [country, setCountry] = useState(gate.country || '');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const join = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/geo/waitlist`, { email, country: country.trim().toUpperCase() || null });
      setDone(true);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not add you to the waitlist. Please check your email address and country.');
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-card text-card-foreground rounded-2xl border border-border/50 shadow-lg p-8" data-testid="country-gate-notice">
      <div className="flex items-start gap-3 mb-4">
        <Globe className="w-6 h-6 text-primary mt-0.5" />
        <div>
          <h2 className="text-lg font-bold" data-testid="country-gate-message">{gate.message || "Insul Nutrients isn't available in your country yet"}</h2>
          {gate.country && <p className="text-sm text-muted-foreground mt-1" data-testid="country-gate-detected">Detected country: {countryLabel(gate.country)} ({gate.country})</p>}
        </div>
      </div>
      {done ? (
        <p className="text-sm font-medium text-green-700" data-testid="waitlist-done">Thanks — we'll let you know when Insul Nutrients launches in your country.</p>
      ) : (
        <form onSubmit={join} className="space-y-4" data-testid="waitlist-form">
          <p className="text-sm">Join the waitlist and we'll tell you when we launch where you are.</p>
          <div className="space-y-2">
            <Label htmlFor="waitlist-email" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Email</Label>
            <Input id="waitlist-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="rounded-xl" data-testid="waitlist-email-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="waitlist-country" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Country (2-letter code)</Label>
            <Input id="waitlist-country" required maxLength={2} value={country} onChange={(e) => setCountry(e.target.value.toUpperCase())} placeholder="e.g. NZ" className="rounded-xl uppercase w-32" data-testid="waitlist-country-input" />
          </div>
          <p className="text-xs text-muted-foreground" data-testid="waitlist-notice">{gate.waitlist_notice}</p>
          <Button type="submit" disabled={busy} className="w-full rounded-full" data-testid="waitlist-submit-button">{busy ? 'Adding…' : 'Notify me'}</Button>
        </form>
      )}
    </div>
  );
};
